/**
 * CetusEd Admin Console — Provision New District
 *
 * COMPLIANCE: FERPA §1232g(b)(4)(B) — New district provisioning requires
 *   a signed DPA before the district can be activated. The provision form
 *   captures the district's legal name, state, and deployment mode.
 *
 *   IDEA §1417(c) — Vendor-hosted records must specify the data processing
 *   terms for each district. These are recorded at provision time.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCreateDistrict } from '../../api/hooks';

export const DistrictProvision: React.FC = () => {
  const navigate = useNavigate();
  const createDistrict = useCreateDistrict();

  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [deploymentMode, setDeploymentMode] = useState<'local-first' | 'vendor-hosted' | 'self-hosted'>('vendor-hosted');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!name.trim() || !state.trim()) {
        setError('District name and state are required.');
        return;
      }

      try {
        const result = await createDistrict.mutateAsync({ name: name.trim(), state: state.trim().toUpperCase(), deploymentMode });
        navigate(`/districts/${result.id}`, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to provision district.');
      }
    },
    [name, state, deploymentMode, createDistrict, navigate],
  );

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/districts">Districts</Link> / Provision New
      </nav>

      <h1>Provision New District</h1>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-field">
          <label htmlFor="district-name">District Name</label>
          <input
            id="district-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Evergreen School District #123"
          />
        </div>

        <div className="form-field">
          <label htmlFor="district-state">State</label>
          <input
            id="district-state"
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            required
            maxLength={2}
            placeholder="WA"
          />
        </div>

        <div className="form-field">
          <label htmlFor="deployment-mode">Deployment Mode</label>
          <select
            id="deployment-mode"
            value={deploymentMode}
            onChange={(e) => setDeploymentMode(e.target.value as typeof deploymentMode)}
          >
            <option value="vendor-hosted">Vendor-Hosted (Cloudflare)</option>
            <option value="self-hosted">Self-Hosted (District)</option>
            <option value="local-first">Local-First (No Server)</option>
          </select>
        </div>

        {error && <p className="form-error" role="alert">{error}</p>}

        <div className="form-actions">
          <button className="admin-btn admin-btn--primary" type="submit" disabled={createDistrict.isPending}>
            {createDistrict.isPending ? 'Provisioning...' : 'Provision District'}
          </button>
          <Link to="/districts" className="admin-btn admin-btn--ghost">Cancel</Link>
        </div>
      </form>
    </div>
  );
};
